import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import { MOCK_PRINTS, mockCrud, loadFromStorage } from '@/services/mockData'
import { relay } from '@/services/relay'
import type { Print, PrintFormData, PrintStatus, FilamentUsageEntry } from '@/types'
import type { RealtimeChannel } from '@supabase/supabase-js'

const STORAGE_KEY = 'prints'

export const usePrintStore = defineStore('print', () => {
  const prints = ref<Print[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const _loaded = ref(false)
  const _contextKey = ref<string>('personal')
  let printsChannel: RealtimeChannel | null = null

  // Getters
  const completedPrints = computed(() => prints.value.filter(p => p.status === 'completed'))
  const activePrints = computed(() => prints.value.filter(p => p.status === 'printing' || p.status === 'paused'))
  const failedPrints = computed(() => prints.value.filter(p => p.status === 'failed'))
  const queuedPrints = computed(() => prints.value.filter(p => p.status === 'queued'))

  const totalPrintTime = computed(() =>
    prints.value.reduce((sum, p) => sum + (p.actual_time || p.estimated_time || 0), 0)
  )

  const getById = (id: string) => prints.value.find(p => p.id === id)

  const fetchPrints = async (force = false, { silent = false, teamId }: { silent?: boolean; teamId?: string } = {}) => {
    const authStore = useAuthStore()
    if (!authStore.userId) {
      console.warn('[printStore] fetchPrints called without userId')
      return
    }

    const newContext = teamId || 'personal'
    if (newContext !== _contextKey.value) {
      _loaded.value = false
      _contextKey.value = newContext
    }

    if (_loaded.value && !force) return

    if (!silent) {
      loading.value = true
      error.value = null
    }

    try {
      if (authStore.isDemoMode) {
        await new Promise(resolve => setTimeout(resolve, 200))
        prints.value = loadFromStorage(STORAGE_KEY, MOCK_PRINTS)
      } else {
        const { supabase } = await import('@/services/supabase')
        let query = supabase
          .from('prints')
          .select('*')
        if (teamId) {
          query = query.eq('team_id', teamId)
        } else {
          query = query.is('team_id', null).eq('user_id', authStore.userId)
        }
        const { data, error: fetchError } = await query
          .order('created_at', { ascending: false })

        if (fetchError) throw fetchError
        prints.value = data as Print[]
      }
      _loaded.value = true
    } catch (err) {
      if (!silent) error.value = err instanceof Error ? err.message : 'Failed to fetch prints'
    } finally {
      if (!silent) loading.value = false
    }
  }

  const clearData = () => {
    prints.value = []
    _loaded.value = false
  }

  const createPrint = async (printData: PrintFormData, teamId?: string) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: false, error: 'Not authenticated' }

    loading.value = true
    try {
      if (authStore.isDemoMode) {
        const newPrint = mockCrud.create(STORAGE_KEY, {
          ...printData,
          user_id: authStore.userId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        } as Print, prints.value)
        prints.value.unshift(newPrint)
        return { success: true, data: newPrint }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: insertError } = await supabase
          .from('prints')
          .insert({ ...printData, user_id: authStore.userId, ...(teamId ? { team_id: teamId } : {}) })
          .select()
          .single()

        if (insertError) throw insertError
        prints.value.unshift(data as Print)
        return { success: true, data }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to create print'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const updatePrint = async (id: string, updates: Partial<PrintFormData>) => {
    const authStore = useAuthStore()
    loading.value = true

    try {
      if (authStore.isDemoMode) {
        const updated = mockCrud.update(STORAGE_KEY, id, updates, prints.value)
        if (updated) {
          const index = prints.value.findIndex(p => p.id === id)
          if (index !== -1) prints.value[index] = updated
        }
        return { success: true, data: updated }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data, error: updateError } = await supabase
          .from('prints')
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('id', id)
          .select()
          .single()

        if (updateError) throw updateError
        const index = prints.value.findIndex(p => p.id === id)
        if (index !== -1) prints.value[index] = data as Print
        return { success: true, data }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update print'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const deletePrint = async (id: string) => {
    const authStore = useAuthStore()
    loading.value = true

    try {
      if (authStore.isDemoMode) {
        mockCrud.delete(STORAGE_KEY, id, prints.value)
        prints.value = prints.value.filter(p => p.id !== id)
        return { success: true }
      } else {
        const { supabase } = await import('@/services/supabase')
        const { error: deleteError } = await supabase.from('prints').delete().eq('id', id)
        if (deleteError) throw deleteError
        prints.value = prints.value.filter(p => p.id !== id)
        return { success: true }
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to delete print'
      return { success: false, error: error.value }
    } finally {
      loading.value = false
    }
  }

  const updateStatus = async (id: string, status: PrintStatus) => {
    const updates: Partial<Print> = { status }

    if (status === 'printing' && !prints.value.find(p => p.id === id)?.start_time) {
      updates.start_time = new Date().toISOString()
    }

    if (status === 'completed' || status === 'failed' || status === 'cancelled') {
      updates.end_time = new Date().toISOString()
    }

    return updatePrint(id, updates as Partial<PrintFormData>)
  }

  /**
   * Save filament/spool assignments for a print (replaces all existing entries).
   * This is the single source of truth for what filaments are associated with a print.
   */
  const saveFilamentAssignments = async (printId: string, entries: FilamentUsageEntry[]) => {
    const authStore = useAuthStore()
    if (!authStore.userId) return { success: true }

    try {
      if (authStore.isDemoMode) {
        const { saveToStorage } = await import('@/services/mockData')
        const all: any[] = loadFromStorage('print_filament_assignments', []) as any[]
        const filtered = all.filter((a: any) => a.print_id !== printId)
        for (const entry of entries) {
          filtered.push({
            id: `pfa-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            user_id: authStore.userId,
            print_id: printId,
            filament_id: entry.filament_id || null,
            spool_id: entry.spool_id || null,
            amount_grams: entry.amount_grams,
            slot_index: entry.slot_index ?? null,
            deducted: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
        }
        saveToStorage('print_filament_assignments', filtered)
        return { success: true }
      } else {
        const { supabase } = await import('@/services/supabase')
        // Delete existing entries then re-insert
        await supabase.from('print_filament_assignments').delete().eq('print_id', printId)

        if (entries.length > 0) {
          const rows = entries.map(entry => ({
            user_id: authStore.userId,
            print_id: printId,
            filament_id: entry.filament_id || null,
            spool_id: entry.spool_id || null,
            amount_grams: entry.amount_grams,
            slot_index: entry.slot_index ?? null,
            deducted: false,
          }))
          const { error: insertError } = await supabase
            .from('print_filament_assignments')
            .insert(rows)
          if (insertError) throw insertError
        }
        return { success: true }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save filament assignments'
      console.error('[printStore]', msg)
      return { success: false, error: msg }
    }
  }

  /**
   * Delete filament assignments for a print (used when deleting a queued print).
   */
  const deleteFilamentAssignments = async (printId: string) => {
    const authStore = useAuthStore()
    try {
      if (authStore.isDemoMode) {
        const assignments = loadFromStorage<any[]>('print_filament_assignments', [])
        const filtered = assignments.filter((a: any) => a.print_id !== printId)
        const { saveToStorage } = await import('@/services/mockData')
        saveToStorage('print_filament_assignments', filtered)
      } else {
        const { supabase } = await import('@/services/supabase')
        await supabase
          .from('print_filament_assignments')
          .delete()
          .eq('print_id', printId)
      }
    } catch (err) {
      console.error('[printStore] Failed to delete filament assignments:', err)
    }
  }

  // ─── Real-time Print Progress ──────────────────────────────────────

  /** Subscribe to real-time updates on prints (progress, status changes from Bridge) */
  /** Sync print status/progress back to the matching print job (scheduler integration) */
  async function syncPrintToJob(print: Print) {
    try {
      const { usePrintJobStore } = await import('@/stores/printJob')
      const printJobStore = usePrintJobStore()

      // Find the printing job on the same printer
      const job = printJobStore.printJobs.find(
        j => j.assigned_printer_id === print.printer_id && j.status === 'printing'
      )
      if (!job) return

      if (print.status === 'completed') {
        await printJobStore.updateJob(job.id, { status: 'done', progress: 100, actual_end: new Date().toISOString() } as any)
      } else if (print.status === 'failed' || print.status === 'cancelled') {
        await printJobStore.updateJob(job.id, { status: 'failed', progress: job.progress, actual_end: new Date().toISOString() } as any)
      } else if (print.status === 'printing' && print.progress > 0) {
        // Update progress on the job (throttled — only if significant change)
        if (Math.abs(print.progress - job.progress) >= 5) {
          await printJobStore.updateJob(job.id, { progress: Math.round(print.progress) } as any)
        }
      }
    } catch { /* printJobStore may not be initialized */ }
  }

  const subscribePrints = async () => {
    const authStore = useAuthStore()
    if (!authStore.userId || authStore.isDemoMode) return

    // Tear down any existing channel before re-subscribing
    if (printsChannel) {
      const { supabase: sb } = await import('@/services/supabase')
      sb.removeChannel(printsChannel)
      printsChannel = null
    }

    const { supabase } = await import('@/services/supabase')

    printsChannel = supabase
      .channel('prints-realtime')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'prints',
          filter: `user_id=eq.${authStore.userId}`
        },
        (payload) => {
          const updated = payload.new as Print
          if (updated.id) {
            const idx = prints.value.findIndex(p => p.id === updated.id)
            if (idx !== -1) {
              prints.value[idx] = { ...prints.value[idx], ...updated }
            }

            // Sync print status/progress to matching print job (if scheduler started this print)
            syncPrintToJob(updated)
          }
        }
      )
      .subscribe()
  }

  const unsubscribePrints = async () => {
    if (printsChannel) {
      const { supabase } = await import('@/services/supabase')
      supabase.removeChannel(printsChannel)
      printsChannel = null
    }
  }

  /** Load filament assignments for a print. */
  const loadFilamentAssignments = async (printId: string): Promise<FilamentUsageEntry[]> => {
    const authStore = useAuthStore()
    try {
      if (authStore.isDemoMode) {
        const all: any[] = loadFromStorage('print_filament_assignments', []) as any[]
        return all
          .filter((r: any) => r.print_id === printId)
          .map((r: any) => ({
            filament_id: r.filament_id || '',
            spool_id: r.spool_id || '',
            amount_grams: r.amount_grams,
            slot_index: r.slot_index ?? undefined,
          }))
      } else {
        const { supabase } = await import('@/services/supabase')
        const { data } = await supabase
          .from('print_filament_assignments')
          .select('filament_id, spool_id, amount_grams, slot_index')
          .eq('print_id', printId)
        return (data || []).map((r: any) => ({
          filament_id: r.filament_id || '',
          spool_id: r.spool_id || '',
          amount_grams: r.amount_grams,
          slot_index: r.slot_index ?? undefined,
        }))
      }
    } catch (err) {
      console.error('[printStore] Failed to load filament assignments:', err)
      return []
    }
  }

  /**
   * Send a queued print to the Bridge via relay for execution.
   * The Bridge will download the gcode from Supabase Storage and send it to the printer.
   */
  /**
   * Print settings sent to Bridge via relay.
   * Payload shape varies by manufacturer — built by usePrintPayloadAdapter.
   * Common fields are typed explicitly; adapter-specific fields pass through via index signature.
   */
  interface PrintSettings {
    /** Base64-encoded decrypted file content — sent when file is encrypted in storage */
    fileData?: string
    /** Adapter-specific fields (amsMapping, materialMappings, etc.) pass through dynamically */
    [key: string]: any
  }

  const sendToPrinter = (printId: string, printerId: string, fileUrl: string, filename: string, settings?: PrintSettings, onProgress?: (percent: number, bytes: number, total: number) => void): Promise<void> => {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        cleanup()
        reject(new Error('Timed out waiting for Bridge response'))
      }, 300_000) // 5 min timeout for large file uploads

      const cleanup = relay.onMessage((msg: any) => {
        if (msg.type === 'print.started' && msg.printId === printId) {
          clearTimeout(timeout)
          cleanup()
          updateStatus(printId, 'printing')
          resolve()
        } else if (msg.type === 'print.error' && msg.printId === printId) {
          clearTimeout(timeout)
          cleanup()
          reject(new Error(msg.error || 'Print failed'))
        } else if (msg.type === 'print.upload_progress' && msg.printId === printId) {
          onProgress?.(msg.percent, msg.bytesTransferred, msg.totalBytes)
        }
      })

      relay.send({
        type: 'print.start',
        printerId,
        printId,
        fileUrl,
        filename,
        ...(settings || {}),
      })
    })
  }

  return {
    prints, loading, error, _loaded, _contextKey,
    completedPrints, activePrints, failedPrints, queuedPrints,
    totalPrintTime, getById,
    fetchPrints, createPrint, updatePrint, deletePrint, updateStatus,
    saveFilamentAssignments, deleteFilamentAssignments, loadFilamentAssignments,
    subscribePrints, unsubscribePrints,
    sendToPrinter,
    clearData
  }
})
